import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/lib/supabase';
import { DollarSign, TrendingUp, CreditCard, Calendar } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';

interface Payment {
  id: string;
  amount: number;
  admin_fee: number;
  teacher_amount: number;
  status: string;
  created_at: string;
  courses: {
    title: string;
  };
  profiles: {
    full_name: string;
    email: string;
  };
}

interface EarningsStats {
  total: number;
  thisMonth: number;
  completedCount: number;
  pendingCount: number;
}

export default function EarningsPage() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [stats, setStats] = useState<EarningsStats>({
    total: 0,
    thisMonth: 0,
    completedCount: 0,
    pendingCount: 0,
  });

  useEffect(() => {
    if (profile?.role !== 'docente') {
      navigate('/dashboard');
      return;
    }
    fetchEarnings();
  }, [profile]);

  const fetchEarnings = async () => {
    const { data: paymentsData } = await supabase
      .from('payments')
      .select(`
        *,
        courses:course_id(title),
        profiles:student_id(full_name, email)
      `)
      .eq('teacher_id', profile?.id)
      .order('created_at', { ascending: false });

    if (paymentsData) {
      setPayments(paymentsData);
      calculateStats(paymentsData);
    }

    setLoading(false);
  };

  const calculateStats = (paymentsData: Payment[]) => {
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    const completed = paymentsData.filter((p) => p.status === 'completed');
    const pending = paymentsData.filter((p) => p.status === 'pending');

    const total = completed.reduce((sum, p) => sum + Number(p.teacher_amount), 0);

    const thisMonth = completed
      .filter((p) => new Date(p.created_at) >= firstDayOfMonth)
      .reduce((sum, p) => sum + Number(p.teacher_amount), 0);

    setStats({
      total,
      thisMonth,
      completedCount: completed.length,
      pendingCount: pending.length,
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-600">Completato</Badge>;
      case 'pending':
        return <Badge variant="secondary">In attesa</Badge>;
      case 'failed':
        return <Badge variant="destructive">Fallito</Badge>;
      case 'refunded':
        return <Badge variant="outline">Rimborsato</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  if (profile?.role !== 'docente') {
    return null;
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
          </div>
          <Skeleton className="h-96" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Guadagni
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Monitora i tuoi guadagni e pagamenti ricevuti
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Guadagni totali
              </CardTitle>
              <DollarSign className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency(stats.total)}
              </div>
              <p className="text-xs text-gray-600 mt-1">
                Da {stats.completedCount} pagamenti completati
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Questo mese
              </CardTitle>
              <TrendingUp className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {formatCurrency(stats.thisMonth)}
              </div>
              <p className="text-xs text-gray-600 mt-1">
                Guadagni di {new Date().toLocaleDateString('it-IT', { month: 'long' })}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Pagamenti completati
              </CardTitle>
              <CreditCard className="h-4 w-4 text-gray-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats.completedCount}
              </div>
              <p className="text-xs text-gray-600 mt-1">
                Transazioni confermate
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                In attesa
              </CardTitle>
              <Calendar className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">
                {stats.pendingCount}
              </div>
              <p className="text-xs text-gray-600 mt-1">
                Pagamenti da confermare
              </p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Storico pagamenti</CardTitle>
            <CardDescription>
              Lista completa di tutti i pagamenti ricevuti
            </CardDescription>
          </CardHeader>
          <CardContent>
            {payments.length === 0 ? (
              <div className="text-center py-12">
                <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">
                  Non hai ancora ricevuto pagamenti
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  I pagamenti appariranno qui quando gli studenti si iscriveranno ai tuoi corsi
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Studente</TableHead>
                      <TableHead>Corso</TableHead>
                      <TableHead>Importo totale</TableHead>
                      <TableHead>Commissione</TableHead>
                      <TableHead>Guadagno</TableHead>
                      <TableHead>Stato</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell className="text-sm">
                          {new Date(payment.created_at).toLocaleDateString('it-IT')}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-sm">
                              {payment.profiles?.full_name || 'N/A'}
                            </p>
                            <p className="text-xs text-gray-500">
                              {payment.profiles?.email}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {payment.courses?.title || 'Corso eliminato'}
                        </TableCell>
                        <TableCell className="text-sm font-medium">
                          {formatCurrency(Number(payment.amount))}
                        </TableCell>
                        <TableCell className="text-sm text-red-600">
                          -{formatCurrency(Number(payment.admin_fee))}
                        </TableCell>
                        <TableCell className="text-sm font-bold text-green-600">
                          {formatCurrency(Number(payment.teacher_amount))}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(payment.status)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
